'use client';

import {
  Users,
  Target,
  DollarSign,
  TrendingUp,
  Calendar,
  Phone,
  Mail,
  CheckCircle2,
  ArrowRight,
  UserPlus,
  Briefcase,
} from 'lucide-react';
import { getCRMStats, leads, opportunities, getUpcomingActivities } from '../data/mock-data';
import { leadStatusConfig, opportunityStatusConfig, activityTypeConfig } from '../types';
import { formatCurrency } from '../../../types';
import type { CRMPage } from '../index';

interface CRMDashboardProps {
  onNavigate: (page: CRMPage) => void;
}

export function CRMDashboard({ onNavigate }: CRMDashboardProps) {
  const stats = getCRMStats();
  const upcomingActivities = getUpcomingActivities();

  // 최근 리드
  const recentLeads = [...leads]
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .slice(0, 5);

  // 진행중인 영업기회
  const activeOpportunities = opportunities
    .filter((o) => o.status !== 'won' && o.status !== 'lost')
    .sort((a, b) => new Date(a.expectedCloseDate).getTime() - new Date(b.expectedCloseDate).getTime())
    .slice(0, 5);

  const getStatusColor = (color: string) => {
    const colors: Record<string, string> = {
      blue: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
      yellow: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
      purple: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
      green: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
      red: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
      gray: 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400',
    };
    return colors[color] || colors.gray;
  };

  return (
    <div className="space-y-6">
      {/* 요약 카드 */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="p-4 rounded-lg bg-card border border-border">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="text-sm text-muted-foreground truncate">신규 리드</div>
              <div className="text-xl font-bold mt-1">{stats.newLeads}건</div>
            </div>
            <div className="w-9 h-9 flex-shrink-0 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
              <UserPlus className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
        </div>

        <div className="p-4 rounded-lg bg-card border border-border">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="text-sm text-muted-foreground truncate">진행중 기회</div>
              <div className="text-xl font-bold mt-1">{stats.openOpportunities}건</div>
            </div>
            <div className="w-9 h-9 flex-shrink-0 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
              <Target className="w-4 h-4 text-purple-600 dark:text-purple-400" />
            </div>
          </div>
        </div>

        <div className="p-4 rounded-lg bg-card border border-border">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="text-sm text-muted-foreground truncate">파이프라인</div>
              <div className="text-xl font-bold mt-1 truncate">{formatCurrency(stats.pipelineValue)}</div>
            </div>
            <div className="w-9 h-9 flex-shrink-0 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
              <DollarSign className="w-4 h-4 text-green-600 dark:text-green-400" />
            </div>
          </div>
        </div>

        <div className="p-4 rounded-lg bg-card border border-border">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="text-sm text-muted-foreground truncate">수주율</div>
              <div className="text-xl font-bold mt-1">{stats.winRate.toFixed(1)}%</div>
            </div>
            <div className="w-9 h-9 flex-shrink-0 rounded-lg bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center">
              <TrendingUp className="w-4 h-4 text-yellow-600 dark:text-yellow-400" />
            </div>
          </div>
        </div>
      </div>

      {/* 성과 지표 */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="p-4 rounded-lg bg-card border border-border">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center">
              <Users className="w-3.5 h-3.5 text-primary" />
            </div>
            <span className="text-sm text-muted-foreground">리드 전환율</span>
          </div>
          <div className="text-xl font-bold">{stats.conversionRate.toFixed(1)}%</div>
          <div className="text-xs text-muted-foreground mt-1">
            전체 {stats.totalLeads}건 중 전환
          </div>
        </div>

        <div className="p-4 rounded-lg bg-card border border-border">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center">
              <Briefcase className="w-3.5 h-3.5 text-primary" />
            </div>
            <span className="text-sm text-muted-foreground">활성 고객</span>
          </div>
          <div className="text-xl font-bold">{stats.activeCustomers}개사</div>
          <div className="text-xs text-muted-foreground mt-1">
            전체 {stats.totalCustomers}개사
          </div>
        </div>

        <div className="p-4 rounded-lg bg-card border border-border">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center">
              <CheckCircle2 className="w-3.5 h-3.5 text-primary" />
            </div>
            <span className="text-sm text-muted-foreground">수주 금액</span>
          </div>
          <div className="text-xl font-bold">{formatCurrency(stats.totalRevenue)}</div>
          <div className="text-xs text-muted-foreground mt-1">이번 달</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 최근 리드 */}
        <div className="p-4 rounded-lg bg-card border border-border">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold flex items-center gap-2">
              <UserPlus className="w-4 h-4" />
              최근 리드
            </h3>
            <button
              onClick={() => onNavigate('leads')}
              className="text-sm text-primary hover:underline flex items-center gap-1"
            >
              전체보기 <ArrowRight className="w-3 h-3" />
            </button>
          </div>
          <div className="space-y-3">
            {recentLeads.map((lead) => {
              const statusConfig = leadStatusConfig[lead.status];
              return (
                <div
                  key={lead.id}
                  className="p-3 rounded-lg bg-accent/50 border border-border"
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium">{lead.name}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(statusConfig.color)}`}>
                      {statusConfig.label}
                    </span>
                  </div>
                  <div className="text-sm text-muted-foreground">{lead.company}</div>
                  <div className="flex items-center justify-between mt-2 text-xs text-muted-foreground">
                    <span>{lead.assignedToName}</span>
                    {lead.expectedRevenue && (
                      <span className="font-medium text-foreground">
                        {formatCurrency(lead.expectedRevenue)}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* 진행중 영업기회 */}
        <div className="p-4 rounded-lg bg-card border border-border">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold flex items-center gap-2">
              <Target className="w-4 h-4" />
              진행중 영업기회
            </h3>
            <button
              onClick={() => onNavigate('opportunities')}
              className="text-sm text-primary hover:underline flex items-center gap-1"
            >
              전체보기 <ArrowRight className="w-3 h-3" />
            </button>
          </div>
          <div className="space-y-3">
            {activeOpportunities.map((opp) => {
              const statusConfig = opportunityStatusConfig[opp.status];
              return (
                <div
                  key={opp.id}
                  className="p-3 rounded-lg bg-accent/50 border border-border"
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium">{opp.name}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(statusConfig.color)}`}>
                      {statusConfig.label}
                    </span>
                  </div>
                  <div className="text-sm text-muted-foreground">{opp.customerName}</div>
                  <div className="flex items-center justify-between mt-2">
                    <div className="flex items-center gap-2">
                      <div className="w-16 h-1.5 bg-muted rounded-full overflow-hidden">
                        <div
                          className="h-full bg-primary rounded-full"
                          style={{ width: `${opp.probability}%` }}
                        />
                      </div>
                      <span className="text-xs text-muted-foreground">{opp.probability}%</span>
                    </div>
                    <span className="text-sm font-medium">{formatCurrency(opp.expectedAmount)}</span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    예상 종료: {opp.expectedCloseDate}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* 예정된 활동 */}
      <div className="p-4 rounded-lg bg-card border border-border">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            예정된 활동
          </h3>
          <button
            onClick={() => onNavigate('activities')}
            className="text-sm text-primary hover:underline flex items-center gap-1"
          >
            전체보기 <ArrowRight className="w-3 h-3" />
          </button>
        </div>
        {upcomingActivities.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {upcomingActivities.map((activity) => {
              const typeConfig = activityTypeConfig[activity.type];
              const TypeIcon = activity.type === 'call' ? Phone : activity.type === 'email' ? Mail : Calendar;
              return (
                <div
                  key={activity.id}
                  className="p-3 rounded-lg bg-accent/50 border border-border"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`p-1.5 rounded ${getStatusColor(typeConfig.color)}`}>
                      <TypeIcon className="w-3 h-3" />
                    </div>
                    <span className="text-xs text-muted-foreground">{typeConfig.label}</span>
                    <span className="text-xs text-muted-foreground ml-auto">
                      {activity.scheduledDate}
                    </span>
                  </div>
                  <div className="font-medium text-sm">{activity.subject}</div>
                  <div className="text-xs text-muted-foreground mt-1">{activity.relatedName}</div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            예정된 활동이 없습니다.
          </div>
        )}
      </div>

      {/* 빠른 작업 */}
      <div className="p-4 rounded-lg bg-card border border-border">
        <h3 className="font-semibold mb-4">빠른 작업</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <button
            onClick={() => onNavigate('leads')}
            className="p-3 rounded-lg bg-accent hover:bg-accent/80 text-left transition-colors"
          >
            <UserPlus className="w-5 h-5 mb-2 text-primary" />
            <div className="text-sm font-medium">리드 등록</div>
          </button>
          <button
            onClick={() => onNavigate('opportunities')}
            className="p-3 rounded-lg bg-accent hover:bg-accent/80 text-left transition-colors"
          >
            <Target className="w-5 h-5 mb-2 text-primary" />
            <div className="text-sm font-medium">기회 등록</div>
          </button>
          <button
            onClick={() => onNavigate('customers')}
            className="p-3 rounded-lg bg-accent hover:bg-accent/80 text-left transition-colors"
          >
            <Users className="w-5 h-5 mb-2 text-primary" />
            <div className="text-sm font-medium">고객 관리</div>
          </button>
          <button
            onClick={() => onNavigate('activities')}
            className="p-3 rounded-lg bg-accent hover:bg-accent/80 text-left transition-colors"
          >
            <Calendar className="w-5 h-5 mb-2 text-primary" />
            <div className="text-sm font-medium">활동 등록</div>
          </button>
        </div>
      </div>
    </div>
  );
}
